import React, { useState, useRef, useMemo, useCallback } from 'react'
import { validateFunc } from '../../constraints/constraints'
import { withTranslation } from 'react-i18next'
import { useMutation, gql, useQuery } from '@apollo/client'
import { createRestaurant, getCuisines, restaurantByOwner } from '../../apollo'
import defaultLogo from '../../assets/img/defaultLogo.png'
import {
  Box,
  Alert,
  Typography,
  Button,
  Checkbox,
  Select,
  MenuItem,
  ListItemText,
  Chip,
  Paper,
  List,
  ListItemButton,
  Switch,
  FormControlLabel
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import ConfigurableValues from '../../config/constants'
import { searchPlaces } from '../../utils/mapbox'
import { SHOP_TYPE } from '../../utils/enums'

// helper: styled input box
const fieldSx = (hasError) => ({
  width: '100%',
  border: `1px solid ${hasError === false ? '#ef4444' : hasError === true ? '#22c55e' : '#e2e8f0'}`,
  borderRadius: '10px',
  px: 1.5,
  py: 0,
  fontSize: '0.875rem',
  color: '#1e293b',
  bgcolor: '#f8fafc',
  outline: 'none',
  height: 42,
  display: 'flex',
  alignItems: 'center',
})

const labelSx = { fontSize: '0.78rem', fontWeight: 600, color: '#475569', mb: 0.5 }

// Defined at module scope so its component identity is stable across re-renders.
// (Defining it inside CreateRestaurant made React remount the <input> on every
// state change, wiping whatever the user had typed.)
const StyledInput = ({ name, placeholder, type = 'text', onChange, endAdornment, hasError }) => (
  <Box sx={{ position: 'relative' }}>
    <Box sx={{ ...fieldSx(hasError), pr: endAdornment ? 5 : 1.5 }}>
      <input
        name={name}
        placeholder={placeholder}
        type={type}
        defaultValue=""
        onChange={onChange}
        style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '0.875rem', color: '#1e293b' }}
      />
    </Box>
    {endAdornment && (
      <Box sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
        {endAdornment}
      </Box>
    )}
  </Box>
)

const CREATE_RESTAURANT = gql`
  ${createRestaurant}
`
const RESTAURANT_BY_OWNER = gql`
  ${restaurantByOwner}
`
const CUISINES = gql`
  ${getCuisines}
`
const CreateRestaurant = props => {
  const { SERVER_URL, MAPBOX_TOKEN } = ConfigurableValues()

  const { t } = props

  const owner = props.owner
  const [showPassword, setShowPassword] = useState(false)
  const [imgUrl, setImgUrl] = useState('')   // preview only
  const [logoUrl, setLogoUrl] = useState('') // preview only
  const [imgFile, setImgFile] = useState(null)
  const [logoFile, setLogoFile] = useState(null)
  const [uploading, setUploading] = useState(false)

  // Address autocomplete via Mapbox forward-geocoding (mirrors VendorProfile.jsx)
  const [addressValue, setAddressValue] = useState('')
  const [addressCoords, setAddressCoords] = useState(null)
  const [predictions, setPredictions] = useState([])
  const [showPredictions, setShowPredictions] = useState(false)
  const searchTimerRef = useRef(null)

  const handleAddressChange = useCallback((value) => {
    setAddressValue(value)
    setAddressCoords(null)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (value.length > 2) {
      searchTimerRef.current = setTimeout(async () => {
        try {
          const results = await searchPlaces(value, MAPBOX_TOKEN, { limit: 5 })
          setPredictions(results)
          setShowPredictions(results.length > 0)
        } catch (e) {
          setPredictions([])
          setShowPredictions(false)
        }
      }, 300)
    } else {
      setPredictions([])
      setShowPredictions(false)
    }
  }, [MAPBOX_TOKEN])

  const handlePredictionSelect = useCallback((prediction) => {
    setAddressValue(prediction.placeName)
    setPredictions([])
    setShowPredictions(false)
    if (prediction.lat != null && prediction.lng != null) {
      setAddressCoords({ latitude: prediction.lat, longitude: prediction.lng })
    }
  }, [])
  const [nameError, setNameError] = useState(null)
  const [usernameError, setUsernameError] = useState(null)
  const [passwordError, setPasswordError] = useState(null)
  const [addressError, setAddressError] = useState(null)
  const [deliveryTimeError, setDeliveryTimeError] = useState(null)
  const [minimumOrderError, setMinimumOrderError] = useState(null)
  const [salesTaxError, setSalesTaxError] = useState(null)
  const [restaurantCuisines, setRestaurantCuisines] = React.useState([])
  // Food truck: no fixed address — it publishes a schedule of stops instead.
  const [isMobile, setIsMobile] = useState(false)

  // const [shopType, setShopType] = useState(SHOP_TYPE.RESTAURANT)
  const [errors, setErrors] = useState('')
  const [success, setSuccess] = useState('')
  const onCompleted = data => {
    console.log('on complete here')
    setNameError(null)
    setAddressError(null)
    setUsernameError(null)
    setPasswordError(null)
    setDeliveryTimeError(null)
    setMinimumOrderError(null)
    setErrors('')
    setSalesTaxError(null)
    setSuccess(t('RestaurantAdded'))
    clearFormValues()
    // Close the modal (and refetch the list) now that the restaurant is saved.
    props.onClose()
  }
  const onError = ({ graphQLErrors, networkError }) => {
    console.log('graphQLErrors', graphQLErrors)
    console.log('networkErrors', networkError)
    setNameError(null)
    setAddressError(null)
    setUsernameError(null)
    setPasswordError(null)
    setDeliveryTimeError(null)
    setMinimumOrderError(null)
    setSalesTaxError(null)
    setSuccess('')
    if (graphQLErrors && graphQLErrors.length) {
      setErrors(graphQLErrors[0].message)
    }
    if (networkError) {
      setErrors(t('NetworkError'))
    }
    setTimeout(hideAlert, 5000)
  }
  const hideAlert = () => {
    setErrors('')
    setSuccess('')
  }

  const { data: cuisines } = useQuery(CUISINES)
  const cuisinesInDropdown = useMemo(
    () => cuisines?.cuisines?.map(item => item.name),
    [cuisines]
  )
  console.log('cuisines => ', cuisinesInDropdown)

  const [mutate, { loading }] = useMutation(CREATE_RESTAURANT, {
    onError,
    onCompleted,
    update
  })

  const formRef = useRef(null)

  const handleFileSelect = (event, type) => {
    const file = event.target.files?.[0]
    if (!file) return
    const preview = URL.createObjectURL(file)
    if (type === 'image') { setImgUrl(preview); setImgFile(file) }
    else { setLogoUrl(preview); setLogoFile(file) }
  }

  // Upload the selected file to the backend's /upload/image endpoint and get a URL.
  // Mirrors VendorProfile.jsx (the working profile-image upload). Cloudinary direct
  // upload was returning undefined here, so banner/logo silently fell back to defaults.
  const uploadFileToBackend = async(file) => {
    if (!file) return null
    const formData = new FormData()
    formData.append('image', file)
    const base = SERVER_URL.endsWith('/') ? SERVER_URL : `${SERVER_URL}/`
    const res = await fetch(`${base}upload/image`, { method: 'POST', body: formData })
    const json = await res.json()
    if (!res.ok) throw new Error(json.error || 'Upload failed')
    return json.url
  }

  const handleCloseModal = () => {
    props.onClose() // Update state to close modal
  };

  const onSubmitValidaiton = data => {
    const form = formRef.current
    const name = form.name.value
    const address = addressValue
    const username = form.username.value
    const password = form.password.value
    // IMPORTANT!!!!
    const deliveryTime = form.deliveryTime.value
    const minimumOrder = form.minimumOrder.value
    const salesTax = +form.salesTax.value

    const nameError = !validateFunc({ name }, 'name')
    const addressError = !validateFunc({ address }, 'address')

    const deliveryTimeError = !validateFunc(
      { deliveryTime: deliveryTime },
      'deliveryTime'
    )
    const minimumOrderError = !validateFunc(
      { minimumOrder: minimumOrder },
      'minimumOrder'
    )
    const usernameError = !validateFunc({ name: username }, 'name')
    const passwordError = !validateFunc({ password }, 'password')
    const salesTaxError = !validateFunc({ salesTax }, 'salesTax')

    if (deliveryTime < 0 || minimumOrder < 0 || salesTax < 0) {
      setDeliveryTimeError(true);
      setMinimumOrderError(true);
      setSalesTaxError(true);
      setErrors(t('Negative Values Not Allowed'));
      return false;
    }

    setNameError(nameError)
    setAddressError(addressError)
    setUsernameError(usernameError)
    setPasswordError(passwordError)
    setDeliveryTimeError(deliveryTimeError)
    setMinimumOrderError(minimumOrderError)
    setSalesTaxError(salesTaxError)
    if (
      !(
        nameError &&
        addressError &&
        usernameError &&
        passwordError &&
        deliveryTimeError &&
        minimumOrderError &&
        salesTaxError
      )
    ) {
      setErrors(t('FieldsRequired'))
    }
    return (
      nameError &&
      addressError &&
      usernameError &&
      passwordError &&
      deliveryTimeError &&
      minimumOrderError &&
      salesTaxError
    )
  }
  function update(cache, { data: { createRestaurant } }) {
    const { restaurantByOwner } = cache.readQuery({
      query: RESTAURANT_BY_OWNER,
      variables: { id: owner }
    })
    cache.writeQuery({
      query: RESTAURANT_BY_OWNER,
      variables: { id: owner },
      data: {
        restaurantByOwner: {
          ...restaurantByOwner,
          restaurants: [...restaurantByOwner.restaurants, createRestaurant]
        }
      }
    })
  }
  const clearFormValues = () => {
    const form = formRef.current
    form.name.value = ''
    form.username.value = ''
    form.password.value = ''
    form.deliveryTime.value = 20
    form.minimumOrder.value = 0
    setAddressValue('')
    setAddressCoords(null)
    setPredictions([])
    setShowPredictions(false)
    setImgUrl('')
    setLogoUrl('')
    setImgFile(null)
    setLogoFile(null)
    setRestaurantCuisines([])
  }

  const handleCuisineChange = event => {
    const {
      target: { value }
    } = event
    setRestaurantCuisines(
      typeof value === 'string' ? value.split(',') : value
    )
  }

  return (
    <Box>
      <form ref={formRef}>
        {/* Row 1: Username + Password */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={labelSx}>{t('Username')}</Typography>
            <StyledInput
              name="username"
              placeholder={t('RestaurantUsername')}
              hasError={usernameError}
              onChange={e => { e.target.value = e.target.value.trim().replace(/\s/g, '') }}
            />
          </Box>
          <Box>
            <Typography sx={labelSx}>{t('Password')}</Typography>
            <StyledInput
              name="password"
              placeholder={t('RestaurantPassword')}
              type={showPassword ? 'text' : 'password'}
              hasError={passwordError}
              endAdornment={
                <Box
                  onClick={() => setShowPassword(v => !v)}
                  sx={{ cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' }}
                >
                  {showPassword ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                </Box>
              }
            />
          </Box>
        </Box>

        {/* Row 2: Name + Address */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={labelSx}>{t('Name')}</Typography>
            <StyledInput name="name" placeholder={t('RestaurantName')} hasError={nameError} />
          </Box>
          <Box sx={{ position: 'relative' }}>
            <Typography sx={labelSx}>{t('Address')}</Typography>
            <Box sx={fieldSx(addressError)}>
              <input
                name="address"
                placeholder={t('RestaurantAddress')}
                value={addressValue}
                onChange={e => handleAddressChange(e.target.value)}
                onFocus={() => predictions.length > 0 && setShowPredictions(true)}
                onBlur={() => setTimeout(() => setShowPredictions(false), 200)}
                autoComplete="off"
                style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '0.875rem', color: '#1e293b' }}
              />
            </Box>
            {showPredictions && predictions.length > 0 && (
              <Paper sx={{ position: 'absolute', zIndex: 1300, width: '100%', maxHeight: 240, overflow: 'auto', mt: 0.5, borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}>
                <List dense disablePadding>
                  {predictions.map(p => (
                    <ListItemButton key={p.id} onMouseDown={() => handlePredictionSelect(p)} sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}>
                      <ListItemText
                        primary={p.name || p.placeName}
                        secondary={p.placeName || ''}
                        primaryTypographyProps={{ fontSize: 13 }}
                        secondaryTypographyProps={{ fontSize: 11 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </Box>

        {/* Row 3: Phone + Delivery Time */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={labelSx}>{t('Phone')}</Typography>
            <StyledInput name="phone" placeholder={t('PhoneNumber')} type="tel" />
          </Box>
          <Box>
            <Typography sx={labelSx}>{t('DeliveryTime')}</Typography>
            <StyledInput name="deliveryTime" placeholder={t('DeliveryTime')} type="number" hasError={deliveryTimeError} />
          </Box>
        </Box>

        {/* Row 4: Min Order + Sales Tax */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={labelSx}>{t('MinOrder')}</Typography>
            <StyledInput name="minimumOrder" placeholder={t('MinOrder')} type="number" hasError={minimumOrderError} />
          </Box>
          <Box>
            <Typography sx={labelSx}>{t('SalesTax')}</Typography>
            <StyledInput name="salesTax" placeholder={t('SalesTax')} type="number" hasError={salesTaxError} />
          </Box>
        </Box>

        {/* Row 5: Shop Category + Cuisines */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={labelSx}>{t('Shop Category')}</Typography>
            <Select
              name="shopType"
              defaultValue={SHOP_TYPE.RESTAURANT}
              size="small"
              sx={{ width: '100%', borderRadius: '10px', bgcolor: '#f8fafc', fontSize: '0.875rem', height: 42 }}
            >
              {Object.values(SHOP_TYPE).map(v => (
                <MenuItem key={v} value={v}>{v}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography sx={labelSx}>{t('Cuisines')}</Typography>
            <Select
              multiple
              value={restaurantCuisines}
              onChange={handleCuisineChange}
              size="small"
              displayEmpty
              renderValue={selected => selected.length === 0
                ? <span style={{ color: '#94a3b8' }}>Select cuisines</span>
                : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(v => <Chip key={v} label={v} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />)}
                  </Box>
              }
              sx={{ width: '100%', borderRadius: '10px', bgcolor: '#f8fafc', fontSize: '0.875rem', minHeight: 42 }}
            >
              {cuisinesInDropdown?.map(cuisine => (
                <MenuItem key={cuisine} value={cuisine}>
                  <Checkbox checked={restaurantCuisines.indexOf(cuisine) > -1} size="small" />
                  <ListItemText primary={cuisine} />
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Row 5b: Food truck. A truck has no permanent address — it publishes a
            schedule of stops instead, so the address above is only where it starts. */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={isMobile} onChange={e => setIsMobile(e.target.checked)} color="success" size="small" />}
            label={<Typography sx={{ fontSize: '0.85rem' }}>This is a food truck</Typography>}
          />
          {isMobile && (
            <Typography sx={{ fontSize: '0.75rem', color: '#166534', mt: 0.25, ml: 0.5 }}>
              The address above is only its starting point — set where it parks each day on the Truck Stops page.
            </Typography>
          )}
        </Box>

        {/* Row 6: Image + Logo upload */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
          {/* Banner image */}
          <Box>
            <Typography sx={labelSx}>{t('UploadAnImage')}</Typography>
            <label htmlFor="file-upload" style={{ cursor: 'pointer', display: 'block' }}>
              <Box sx={{
                height: 120, borderRadius: '12px', border: '2px dashed #e2e8f0',
                bgcolor: '#f8fafc', overflow: 'hidden', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                '&:hover': { borderColor: '#818cf8', bgcolor: '#f5f3ff' }
              }}>
                {imgUrl
                  ? <img src={imgUrl} alt="banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <Box sx={{ textAlign: 'center' }}>
                      <AddPhotoAlternateIcon sx={{ color: '#cbd5e1', fontSize: 32, mb: 0.5 }} />
                      <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>Click to upload</Typography>
                    </Box>
                }
              </Box>
            </label>
            <input id="file-upload" type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => handleFileSelect(e, 'image')} />
          </Box>

          {/* Logo */}
          <Box>
            <Typography sx={labelSx}>{t('UploadaLogo')}</Typography>
            <label htmlFor="logo-upload" style={{ cursor: 'pointer', display: 'block' }}>
              <Box sx={{
                height: 120, borderRadius: '12px', border: '2px dashed #e2e8f0',
                bgcolor: '#f8fafc', overflow: 'hidden', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                '&:hover': { borderColor: '#818cf8', bgcolor: '#f5f3ff' }
              }}>
                {logoUrl
                  ? <img src={logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <Box sx={{ textAlign: 'center' }}>
                      <AddPhotoAlternateIcon sx={{ color: '#cbd5e1', fontSize: 32, mb: 0.5 }} />
                      <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>Click to upload</Typography>
                    </Box>
                }
              </Box>
            </label>
            <input id="logo-upload" type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => handleFileSelect(e, 'logo')} />
          </Box>
        </Box>

        {/* Alerts */}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{success}</Alert>}
        {errors && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{errors}</Alert>}

        {/* Submit */}
        <Button
          fullWidth
          disabled={loading || uploading}
          onClick={async e => {
            e.preventDefault()
            if (onSubmitValidaiton()) {
              const form = formRef.current
              setUploading(true)
              let imgUpload, logoUpload
              try {
                imgUpload = await uploadFileToBackend(imgFile)
                logoUpload = await uploadFileToBackend(logoFile)
              } catch (err) {
                setUploading(false)
                setErrors(err.message || t('NetworkError'))
                setTimeout(hideAlert, 5000)
                return
              }
              setUploading(false)
              mutate({
                variables: {
                  owner,
                  restaurant: {
                    name: form.name.value,
                    address: addressValue,
                    ...(addressCoords ? { location: { latitude: addressCoords.latitude, longitude: addressCoords.longitude } } : {}),
                    phone: form.phone.value,
                    image: imgUpload || 'https://enatega.com/wp-content/uploads/2023/11/man-suit-having-breakfast-kitchen-side-view.webp',
                    logo: logoUpload || defaultLogo,
                    deliveryTime: Number(form.deliveryTime.value),
                    minimumOrder: Number(form.minimumOrder.value),
                    salesTax: Number(form.salesTax.value),
                    username: form.username.value,
                    password: form.password.value,
                    shopType: form.shopType.value,
                    cuisines: restaurantCuisines,
                    // A food truck parks somewhere different every day; its
                    // address is set per stop on the Truck Stops page, and the
                    // one entered above is only its starting point.
                    isMobile
                  }
                }
              })
            }
          }}
          sx={{
            bgcolor: '#f97316', color: '#fff', fontWeight: 700, borderRadius: '12px',
            py: 1.4, fontSize: '0.9rem', textTransform: 'none',
            '&:hover': { bgcolor: '#ea6c0a' },
            '&:disabled': { bgcolor: '#fed7aa', color: '#fff' },
            boxShadow: '0 4px 12px rgba(249,115,22,0.35)'
          }}
        >
          {uploading ? 'Uploading...' : loading ? 'Saving...' : t('Save')}
        </Button>
      </form>
    </Box>
  )
}
export default withTranslation()(CreateRestaurant)
